'use client';
import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import { visitorService } from '@/lib/services/golfService';
import { emailService } from '@/lib/services/emailService';
import { useAuth } from '@/contexts/AuthContext';
import { trackAuth } from '@/lib/analytics';

interface RegisterValues {
  firstName: string;
  surname: string;
  email: string;
  mobile: string;
  dateOfBirth: string;
  idNumber: string;
  address: string;
  emergencyContact: string;
  emergencyPhone: string;
  hasMembership: string;
  homeClub: string;
  handicap: string;
  cdhNumber: string;
  password: string;
  confirmPassword: string;
  newsletter: boolean;
  promotions: boolean;
  events: boolean;
  competition: boolean;
  acceptRules: boolean;
  acceptIndemnity: boolean;
  acceptTerms: boolean;
}

interface Props {
  onSwitchToLogin: () => void;
}

function getAgeCategory(dob: string): 'Junior' | 'Adult' | 'Senior' | null {
  if (!dob) return null;
  const birth = new Date(dob);
  const today = new Date(2026, 5, 10);
  const age = today.getFullYear() - birth.getFullYear() - (today < new Date(today.getFullYear(), birth.getMonth(), birth.getDate()) ? 1 : 0);
  if (age < 18) return 'Junior';
  if (age >= 60) return 'Senior';
  return 'Adult';
}

const catStyles: Record<string, string> = {
  Junior: 'badge-junior',
  Adult: 'badge-adult',
  Senior: 'badge-senior',
};

export default function RegisterForm({ onSwitchToLogin }: Props) {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [ageCategory, setAgeCategory] = useState<'Junior' | 'Adult' | 'Senior' | null>(null);
  const { signUp } = useAuth();
  const router = useRouter();

  const { register, handleSubmit, watch, formState: { errors } } = useForm<RegisterValues>({
    defaultValues: { hasMembership: 'no' },
  });

  const dob = watch('dateOfBirth');
  const password = watch('password');
  const hasMembership = watch('hasMembership');

  useEffect(() => {
    setAgeCategory(getAgeCategory(dob));
  }, [dob]);

  const onSubmit = async (data: RegisterValues) => {
    setIsLoading(true);
    try {
      const category = getAgeCategory(data.dateOfBirth) || 'Adult';
      const marketingOptIn = !!(data.newsletter || data.promotions || data.events || data.competition);
      const fullName = `${data.firstName} ${data.surname}`;

      // Step 1: Create Supabase auth account
      await signUp(data.email, data.password, { fullName });
      trackAuth('register', 'email');

      // Step 2: Create visitor profile record
      await visitorService.upsertByEmail({
        firstName: data.firstName,
        surname: data.surname,
        email: data.email,
        mobile: data.mobile,
        dateOfBirth: data.dateOfBirth,
        idNumber: data.idNumber,
        address: data.address,
        emergencyContact: data.emergencyContact,
        emergencyPhone: data.emergencyPhone,
        homeClub: data.hasMembership === 'yes' ? data.homeClub : '',
        handicap: data.handicap ? parseFloat(data.handicap) : 0,
        cdhNumber: data.cdhNumber,
        category,
        marketingOptIn,
      });

      // Step 3: Send registration verification email to visitor + admin notification
      const registeredAt = new Date().toLocaleDateString('en-ZA', {
        weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
      });
      await Promise.all([
        emailService.sendRegistrationVerification({
          to: data.email,
          visitorName: fullName,
          email: data.email,
          mobile: data.mobile,
          category,
          handicap: data.handicap ? parseFloat(data.handicap) : undefined,
        }),
        emailService.notifyAdminRegistration({
          visitorName: fullName,
          email: data.email,
          mobile: data.mobile,
          category,
          handicap: data.handicap ? parseFloat(data.handicap) : undefined,
          registeredAt,
        }),
      ]);

      toast.success(`Welcome to MGC, ${data.firstName}! Your account has been created.`);
      router.push('/member-portal');
      router.refresh();
    } catch (error: any) {
      toast.error(error?.message || 'Registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 max-w-md">
      {/* Account Credentials */}
      <div className="bg-card border border-border rounded-2xl p-5">
        <h3 className="text-sm font-bold text-foreground mb-4 flex items-center gap-2">
          <Icon name="LockClosedIcon" size={15} variant="outline" className="text-primary" />
          Account Credentials
        </h3>
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-semibold text-foreground mb-1.5">Email Address <span className="text-red-500">*</span></label>
            <input
              type="email"
              className={`input-field ${errors.email ? 'error' : ''}`}
              placeholder="your@email.co.za"
              {...register('email', {
                required: 'Email is required',
                pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' },
              })}
            />
            {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>}
          </div>

          <div>
            <label className="block text-sm font-semibold text-foreground mb-1.5">Password <span className="text-red-500">*</span></label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                className={`input-field pr-11 ${errors.password ? 'error' : ''}`}
                placeholder="Minimum 8 characters"
                {...register('password', {
                  required: 'Password is required',
                  minLength: { value: 8, message: 'Password must be at least 8 characters' },
                  pattern: { value: /^(?=.*[A-Za-z])(?=.*\d)/, message: 'Include at least one letter and one number' },
                })}
              />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" aria-label="Toggle password">
                <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={18} variant="outline" />
              </button>
            </div>
            {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password.message}</p>}
          </div>

          <div>
            <label className="block text-sm font-semibold text-foreground mb-1.5">Confirm Password <span className="text-red-500">*</span></label>
            <div className="relative">
              <input
                type={showConfirm ? 'text' : 'password'}
                className={`input-field pr-11 ${errors.confirmPassword ? 'error' : ''}`}
                placeholder="Repeat your password"
                {...register('confirmPassword', {
                  required: 'Please confirm your password',
                  validate: (v) => v === password || 'Passwords do not match',
                })}
              />
              <button type="button" onClick={() => setShowConfirm(!showConfirm)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" aria-label="Toggle confirm password">
                <Icon name={showConfirm ? 'EyeSlashIcon' : 'EyeIcon'} size={18} variant="outline" />
              </button>
            </div>
            {errors.confirmPassword && <p className="text-red-500 text-xs mt-1">{errors.confirmPassword.message}</p>}
          </div>
        </div>
      </div>

      {/* Personal Information */}
      <div className="bg-card border border-border rounded-2xl p-5">
        <h3 className="text-sm font-bold text-foreground mb-4 flex items-center gap-2">
          <Icon name="UserIcon" size={15} variant="outline" className="text-primary" />
          Personal Information
        </h3>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-semibold text-foreground mb-1.5">First Name <span className="text-red-500">*</span></label>
              <input type="text" className={`input-field ${errors.firstName ? 'error' : ''}`} placeholder="First name"
                {...register('firstName', { required: 'Required' })} />
              {errors.firstName && <p className="text-red-500 text-xs mt-1">{errors.firstName.message}</p>}
            </div>
            <div>
              <label className="block text-sm font-semibold text-foreground mb-1.5">Surname <span className="text-red-500">*</span></label>
              <input type="text" className={`input-field ${errors.surname ? 'error' : ''}`} placeholder="Surname"
                {...register('surname', { required: 'Required' })} />
              {errors.surname && <p className="text-red-500 text-xs mt-1">{errors.surname.message}</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-semibold text-foreground mb-1.5">Date of Birth <span className="text-red-500">*</span></label>
              <input type="date" className={`input-field ${errors.dateOfBirth ? 'error' : ''}`}
                {...register('dateOfBirth', { required: 'Required' })} />
              {errors.dateOfBirth && <p className="text-red-500 text-xs mt-1">{errors.dateOfBirth.message}</p>}
              {ageCategory && (
                <div className="mt-1.5 flex items-center gap-1.5">
                  <span className="text-xs text-muted-foreground">Category:</span>
                  <span className={catStyles[ageCategory]}>{ageCategory}</span>
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-semibold text-foreground mb-1.5">SA ID Number <span className="text-red-500">*</span></label>
              <input type="text" maxLength={13} className={`input-field ${errors.idNumber ? 'error' : ''}`} placeholder="13-digit ID"
                {...register('idNumber', { required: 'Required', pattern: { value: /^\d{13}$/, message: '13 digits required' } })} />
              {errors.idNumber && <p className="text-red-500 text-xs mt-1">{errors.idNumber.message}</p>}
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-foreground mb-1.5">Mobile Number <span className="text-red-500">*</span></label>
            <input type="tel" className={`input-field ${errors.mobile ? 'error' : ''}`} placeholder="e.g. 082 345 6789"
              {...register('mobile', { required: 'Required', pattern: { value: /^0\d{9}$/, message: 'Valid SA mobile required' } })} />
            {errors.mobile && <p className="text-red-500 text-xs mt-1">{errors.mobile.message}</p>}
          </div>

          <div>
            <label className="block text-sm font-semibold text-foreground mb-1.5">Residential Address <span className="text-red-500">*</span></label>
            <input type="text" className={`input-field ${errors.address ? 'error' : ''}`} placeholder="Street address, PMB"
              {...register('address', { required: 'Required' })} />
            {errors.address && <p className="text-red-500 text-xs mt-1">{errors.address.message}</p>}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-semibold text-foreground mb-1.5">Emergency Contact <span className="text-red-500">*</span></label>
              <input type="text" className={`input-field ${errors.emergencyContact ? 'error' : ''}`} placeholder="Full name"
                {...register('emergencyContact', { required: 'Required' })} />
              {errors.emergencyContact && <p className="text-red-500 text-xs mt-1">{errors.emergencyContact.message}</p>}
            </div>
            <div>
              <label className="block text-sm font-semibold text-foreground mb-1.5">Emergency Number <span className="text-red-500">*</span></label>
              <input type="tel" className={`input-field ${errors.emergencyPhone ? 'error' : ''}`} placeholder="e.g. 073 111 2233"
                {...register('emergencyPhone', { required: 'Required' })} />
              {errors.emergencyPhone && <p className="text-red-500 text-xs mt-1">{errors.emergencyPhone.message}</p>}
            </div>
          </div>
        </div>
      </div>

      {/* Golf Information */}
      <div className="bg-card border border-border rounded-2xl p-5">
        <h3 className="text-sm font-bold text-foreground mb-4 flex items-center gap-2">
          <span className="text-base">⛳</span>
          Golf Information
        </h3>
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-semibold text-foreground mb-2">Club Membership</label>
            <div className="flex gap-3">
              {[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }].map((opt) => (
                <label key={`mem-${opt.value}`} className={`flex-1 flex items-center gap-2 p-3 rounded-xl border-2 cursor-pointer transition-all ${hasMembership === opt.value ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/30'}`}>
                  <input type="radio" value={opt.value} className="sr-only" {...register('hasMembership')} />
                  <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${hasMembership === opt.value ? 'border-primary' : 'border-border'}`}>
                    {hasMembership === opt.value && <div className="w-2 h-2 rounded-full bg-primary" />}
                  </div>
                  <span className="text-sm font-medium text-foreground">{opt.label}</span>
                </label>
              ))}
            </div>
          </div>

          {hasMembership === 'yes' && (
            <div>
              <label className="block text-sm font-semibold text-foreground mb-1.5">Home Club Name</label>
              <input type="text" className="input-field" placeholder="e.g. Royal Durban Golf Club" {...register('homeClub')} />
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-semibold text-foreground mb-1.5">Handicap Index</label>
              <input type="number" step="0.1" min="0" max="54" className="input-field" placeholder="e.g. 14.2" {...register('handicap')} />
            </div>
            <div>
              <label className="block text-sm font-semibold text-foreground mb-1.5">CDH Number</label>
              <input type="text" className="input-field" placeholder="e.g. CDH-29341" {...register('cdhNumber')} />
            </div>
          </div>
        </div>
      </div>

      {/* Marketing Preferences */}
      <div className="bg-card border border-border rounded-2xl p-5">
        <h3 className="text-sm font-bold text-foreground mb-3 flex items-center gap-2">
          <Icon name="MegaphoneIcon" size={15} variant="outline" className="text-primary" />
          Marketing Preferences (Optional)
        </h3>
        <div className="space-y-2">
          {[
            { id: 'newsletter', label: 'MGC Newsletter' },
            { id: 'promotions', label: 'Promotional Offers & Specials' },
            { id: 'events', label: 'Event Notifications' },
            { id: 'competition', label: 'Competition Updates' },
          ].map((opt) => (
            <label key={`mkt-reg-${opt.id}`} className="flex items-center gap-3 cursor-pointer py-1">
              <input type="checkbox" className="w-4 h-4 rounded border-border accent-primary" {...register(opt.id as keyof RegisterValues)} />
              <span className="text-sm text-foreground">{opt.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Required Consents */}
      <div className="bg-card border border-border rounded-2xl p-5">
        <h3 className="text-sm font-bold text-foreground mb-3 flex items-center gap-2">
          <Icon name="DocumentTextIcon" size={15} variant="outline" className="text-red-500" />
          Required Consents
        </h3>
        <div className="space-y-2">
          {[
            { id: 'acceptRules', label: 'I accept the Club Rules and Regulations' },
            { id: 'acceptIndemnity', label: 'I accept the Liability and Indemnity Conditions' },
            { id: 'acceptTerms', label: 'I accept the Terms and Conditions of Play' },
          ].map((consent) => (
            <div key={`consent-reg-${consent.id}`}>
              <label className={`flex items-start gap-3 cursor-pointer py-1.5 ${errors[consent.id as keyof RegisterValues] ? 'text-red-500' : ''}`}>
                <input
                  type="checkbox"
                  className="w-4 h-4 rounded border-border accent-primary mt-0.5"
                  {...register(consent.id as keyof RegisterValues, { required: 'This consent is required to register' })}
                />
                <span className="text-sm text-foreground">{consent.label}</span>
              </label>
              {errors[consent.id as keyof RegisterValues] && (
                <p className="text-red-500 text-xs ml-7">{(errors[consent.id as keyof RegisterValues] as {message?: string})?.message}</p>
              )}
            </div>
          ))}
        </div>
      </div>

      <button
        type="submit"
        disabled={isLoading}
        className="w-full btn-primary py-3.5 rounded-xl text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-70"
      >
        {isLoading ? (
          <>
            <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            Creating Your Account...
          </>
        ) : (
          <>
            <Icon name="UserPlusIcon" size={18} variant="outline" />
            Create Visitor Account
          </>
        )}
      </button>

      <p className="text-center text-sm text-muted-foreground">
        Already registered?{' '}
        <button type="button" onClick={onSwitchToLogin} className="text-primary font-semibold hover:text-secondary transition-colors">
          Sign in here
        </button>
      </p>

      <p className="text-center text-xs text-muted-foreground">
        By registering, you agree to our{' '}
        <a href="#" className="text-primary hover:underline">Privacy Policy</a>{' '}
        and{' '}
        <a href="#" className="text-primary hover:underline">Terms of Use</a>.
        Your data is protected under POPIA.
      </p>
    </form>
  );
}